import {Separator} from 'platform/components';
import {Box, Center, Hide, HStack, Image, Show, Stack, Text, VStack} from 'platform/foundation';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../i18n/i18n';
import {useIsPrintForced} from '../../../../pages/Overview/hooks/useIsPrintForced';
import {FooterHeading} from './components/FooterHeading';
import {FooterLink} from './components/FooterLink';
import {PaymentLogos} from './components/PaymentLogos';
import {SocialNetworks} from './components/SocialNetworks';

const SHOW_SOCIAL_NETWORKS = false;
const SHOW_PAYMENT_METHODS = false;

export function Footer(props: TestIdProps) {
  const {isPrint} = useIsPrintForced();

  return (
    <Hide when={isPrint} whenFeatureDisabled="pricereport_footer">
      <Box
        borderTop="1px solid"
        borderColor="general.separator"
        backgroundColor="palettes.neutral.20.100"
        paddingTop={[8, 20]}
        paddingBottom={8}
        paddingHorizontal={8}
        data-testid={suffixTestId('wrapper', props)}
      >
        <Center>
          <VStack spacing={[8, 20]} width="100%" maxWidth={320}>
            <Stack direction={['column', 'row']} spacing={[10, 6, 8]}>
              <VStack spacing={8} grow={3}>
                <Image
                  src="assets/images/carsdata-logo.svg"
                  width={48}
                  data-testid={suffixTestId('carsdata-logo', props)}
                />
                <Text color="tertiary" size="small" data-testid={suffixTestId('copyright', props)}>
                  {i18n.t('entity.footer.copyright')}
                </Text>
                <Show when={SHOW_SOCIAL_NETWORKS}>
                  <SocialNetworks data-testid={suffixTestId('socialNetworks', props)} />
                </Show>
              </VStack>
              <HStack spacing={[4, 6, 8]} grow={2} data-testid={suffixTestId('links', props)}>
                <VStack spacing={6} grow={1} basis={0}>
                  <FooterHeading data-testid={suffixTestId('services', props)}>
                    {i18n.t('entity.footer.services')}
                  </FooterHeading>
                  <VStack spacing={3}>
                    <FooterLink
                      href="https://cz.cebia.com/"
                      data-testid={suffixTestId('autotracer', props)}
                    >
                      Autotracer
                    </FooterLink>
                    <FooterLink
                      href="https://eag.group/cs/projects.html#alpha"
                      data-testid={suffixTestId('priceReport', props)}
                    >
                      Price Report
                    </FooterLink>
                  </VStack>
                </VStack>
                <VStack spacing={6} grow={1} basis={0}>
                  <FooterHeading data-testid={suffixTestId('company', props)}>
                    {i18n.t('entity.footer.company')}
                  </FooterHeading>
                  <VStack spacing={3}>
                    <FooterLink
                      href="https://eag.group/cs/projects.html#contact"
                      data-testid={suffixTestId('contact', props)}
                    >
                      {i18n.t('entity.footer.contact')}
                    </FooterLink>
                    {/* links with href undefined are hidden – so far we don't have where to link them */}
                    <FooterLink
                      href={undefined}
                      data-testid={suffixTestId('termsAndConditions', props)}
                    >
                      {i18n.t('entity.footer.termsAndConditions')}
                    </FooterLink>
                  </VStack>
                </VStack>
              </HStack>
            </Stack>
            <VStack spacing={8}>
              <Separator />
              <Show when={SHOW_PAYMENT_METHODS}>
                <Stack
                  direction={['column', 'row']}
                  justify="space-between"
                  align="center"
                  spacing={8}
                  maxWidth="100%"
                  data-testid={suffixTestId('paymentMethods', props)}
                >
                  <Box flex={1}>
                    <Text
                      color="secondary"
                      size="xSmall"
                      data-testid={suffixTestId('payment', props)}
                    >
                      {i18n.t('entity.footer.payment')}
                    </Text>
                  </Box>
                  <PaymentLogos data-testid={suffixTestId('paymentLogos', props)} />
                </Stack>
              </Show>
              <Text color="tertiary" size="xSmall" data-testid={suffixTestId('disclaimer', props)}>
                {i18n.t('entity.footer.disclaimer')}
              </Text>
            </VStack>
          </VStack>
        </Center>
      </Box>
    </Hide>
  );
}
